import React, { useState, useEffect, useRef } from 'react';
import { videosAPI } from '../../services/api';

/**
 * MuxThumbnailFramePicker
 *
 * Renders a frame-time picker for Mux videos. Admins can scrub to any second
 * and see a live preview of that frame before saving.
 *
 * Props:
 *   video         - the video object (must have id, platform, metadata)
 *   thumbnailTime - current thumbnail_time value (number | null)
 *   onTimeChange  - callback(newTime: number) called immediately on change
 *
 * Only renders for platform === 'mux'. Returns null for all other platforms.
 */
const MuxThumbnailFramePicker = ({ video, thumbnailTime, onTimeChange }) => {
  // All hooks must be declared BEFORE any early return (react-hooks/rules-of-hooks)
  const [previewSrc, setPreviewSrc] = useState('');
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  // Fetch the preview URL from the backend (backend signs the Mux token)
  const fetchPreview = (time) => {
    setLoading(true);
    videosAPI
      .thumbnailPreview(video.id, time)
      .then((res) => {
        setPreviewSrc(res.data.thumbnail_url);
      })
      .catch(() => {
        // Silently fail — preview just won't update
      })
      .finally(() => setLoading(false));
  };

  // Handle time change: notify parent immediately, debounce the API call
  const handleTimeChange = (rawValue) => {
    const numericTime = parseFloat(rawValue);
    if (isNaN(numericTime)) return;

    // Immediately update parent state (so save includes the new value)
    onTimeChange(numericTime);

    // Debounce the preview fetch (~300ms)
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchPreview(numericTime);
    }, 300);
  };

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  // Only relevant for Mux videos — early return AFTER all hooks (rules-of-hooks)
  if (!video || video.platform !== 'mux') return null;

  const duration = video.metadata?.duration || null;

  return (
    <div className="mt-4 space-y-3">
      <label className="block text-gray-400 text-xs font-medium uppercase tracking-wider">
        Thumbnail Frame
        <span className="text-gray-600 font-normal ml-2 normal-case">
          — pick the frame shown as the video thumbnail
        </span>
      </label>

      {/* Numeric seconds input */}
      <div className="flex items-center gap-3">
        <input
          type="number"
          data-testid="thumbnail-time-input"
          value={thumbnailTime !== null && thumbnailTime !== undefined ? thumbnailTime : ''}
          onChange={(e) => handleTimeChange(e.target.value)}
          min="0"
          step="0.5"
          placeholder="seconds (e.g. 12.5)"
          className="w-40 bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-2 focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
        />
        <span className="text-gray-500 text-sm">sec</span>
        {loading && (
          <span className="text-gray-500 text-xs animate-pulse">Loading preview...</span>
        )}
      </div>

      {/* Range slider — only when Mux metadata includes duration */}
      {duration && (
        <input
          type="range"
          data-testid="thumbnail-time-slider"
          value={thumbnailTime !== null && thumbnailTime !== undefined ? thumbnailTime : 0}
          onChange={(e) => handleTimeChange(e.target.value)}
          min="0"
          max={duration}
          step="0.5"
          className="w-full accent-red-500"
        />
      )}

      {/* Live preview image */}
      <img
        data-testid="thumbnail-preview"
        src={previewSrc}
        alt="Thumbnail frame preview"
        className="rounded-lg border border-gray-700 max-w-xs w-full object-cover"
        style={{ minHeight: previewSrc ? 'auto' : '80px', background: '#1f2937' }}
      />
    </div>
  );
};

export default MuxThumbnailFramePicker;
